import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BUILDING_OPTIONS, buildingForGrade } from '../../utils/schoolBuilding';
import axios from 'axios';
import './MigrantQuestionnaire.css';

/**
 * DESE Title I.C Migrant Education Parent Questionnaire (MO 500-3129).
 *
 * Missouri requires this at enrollment for every student, and for the large
 * majority of families every answer will be "No". So the job of this screen is to
 * be honest and quick: the three required questions up front, household details
 * pre-filled from what the parent already told us, and the long job list present
 * (it is on the official form) but clearly optional.
 *
 * The option list and question wording come from the SERVER per language, because
 * the English, Spanish and French forms are separate revisions that word the
 * questions differently and group the work differently (11 / 11 / 9 options). We
 * must not invent or translate any of it here.
 *
 * Job selection is MULTI-select: all three versions say "choose all that apply".
 */
export const MIGRANT_REQUIRED_QUESTIONS = ['movedIntoDistrict', 'workedAgFishing', 'plansAgFishing'];

/**
 * Every line the official form has, other than the job pictures, has to be filled in.
 * The pictures are the one genuine exception: they are "choose all that apply", and a
 * family with no farm work correctly selects none of them.
 */
export const MIGRANT_REQUIRED_TEXT = [
  'parentsGuardians', 'address', 'city', 'state', 'zip', 'homePhone',
  'placeOfEmployment', 'childrenInHome', 'schoolBuilding'
];

/**
 * Lines that are only required once an earlier answer makes them meaningful.
 *
 * `dateOfMove` is the date the family moved INTO the district, so a family that
 * answered "No" to having moved has no such date and used to be stuck: the field
 * demanded an answer that does not exist. It stays visible either way - the line is
 * on the official form, and a family can correct the question above it - but it only
 * blocks submission when they said they moved.
 */
export const MIGRANT_CONDITIONAL_TEXT = {
  dateOfMove: (answers) => answers.movedIntoDistrict === true
};

/** The text lines this particular set of answers has to fill in. */
export const requiredMigrantText = (answers = {}) => [
  ...MIGRANT_REQUIRED_TEXT,
  ...Object.keys(MIGRANT_CONDITIONAL_TEXT).filter((k) => MIGRANT_CONDITIONAL_TEXT[k](answers))
];

/**
 * Labels live here, next to the fields, so the sticky "still to fill in" bar in the
 * combined enrollment form cannot drift out of step with the questionnaire itself.
 */
export const MIGRANT_FIELD_LABELS = {
  movedIntoDistrict: 'Moved to the district in the past 3 years?',
  workedAgFishing: 'Worked in agriculture or fishing?',
  plansAgFishing: 'Plans agriculture or fishing work soon?',
  parentsGuardians: 'Parents / guardians',
  address: 'Address',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
  homePhone: 'Home phone',
  placeOfEmployment: 'Place of employment',
  childrenInHome: 'Number of children in home',
  dateOfMove: 'Date of move',
  schoolBuilding: 'School building'
};

/**
 * How many answers the questionnaire requires in total. Exported so the enrollment form's
 * progress bar can count this block without re-deriving the list and drifting from it.
 *
 * Answer-dependent, because the conditional lines above are only part of the job for the
 * families they apply to - counting them for everyone would leave the bar short of the end.
 */
export const migrantTotalRequired = (answers = {}) =>
  MIGRANT_REQUIRED_QUESTIONS.length + requiredMigrantText(answers).length;

/** Everything still unanswered: the three questions, then the required text lines. */
export const missingMigrantAnswers = (answers = {}) => [
  ...MIGRANT_REQUIRED_QUESTIONS.filter((k) => answers[k] !== true && answers[k] !== false),
  ...requiredMigrantText(answers).filter((k) => String(answers[k] ?? '').trim() === '')
];

export const emptyMigrantAnswers = () => ({
  movedIntoDistrict: null,
  workedAgFishing: null,
  plansAgFishing: null,
  jobTypes: [],
  jobTypesOther: '',
  parentsGuardians: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  homePhone: '',
  placeOfEmployment: '',
  childrenInHome: '',
  dateOfMove: '',
  // Which building this child attends. The questionnaire is the only place we ask,
  // and the official form has a column for it per student.
  schoolBuilding: ''
});

/**
 * Renders standalone (its own form + submit) or EMBEDDED inside another form, where
 * it must not render a nested <form> and the host owns submission. In embedded mode
 * answers and language are controlled by the host so they can be submitted together.
 */
const MigrantQuestionnaire = ({
  studentIntakeId,
  studentName,
  studentGrade,
  onComplete,
  onCancel,
  embedded = false,
  answers: answersProp,
  onAnswersChange,
  language: languageProp,
  onLanguageChange,
  externalError
}) => {
  const [internalLanguage, setInternalLanguage] = useState('en');
  const language = languageProp || internalLanguage;
  const setLanguage = (v) => (onLanguageChange ? onLanguageChange(v) : setInternalLanguage(v));
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [internalAnswers, setInternalAnswers] = useState(emptyMigrantAnswers);
  const answers = answersProp || internalAnswers;

  // loadDefinition is memoized, so a setter that closed over `answers` directly would
  // capture the value from first render and could compute the language-switch filter
  // from stale selections. Read through a ref so the setter is always current.
  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  const setAnswers = useCallback((updater) => {
    const current = answersRef.current;
    const next = typeof updater === 'function' ? updater(current) : updater;
    answersRef.current = next;
    if (onAnswersChange) onAnswersChange(next);
    else setInternalAnswers(next);
  }, [onAnswersChange]);

  // Wording and options for the chosen language. Refetched on language change so the
  // parent always sees the questions from the form they will actually be signing.
  const loadDefinition = useCallback(async (lang) => {
    const res = await axios.get(`/api/migrant-questionnaire/definition?language=${lang}`);
    setDefinition(res.data);
    // Keep only selections that exist in the newly chosen language: the revisions do
    // not share option keys, e.g. French has no separate "potatoes".
    setAnswers((prev) => {
      const valid = new Set(res.data.jobOptions.map((o) => o.key));
      const kept = prev.jobTypes.filter((k) => valid.has(k));
      return kept.length === prev.jobTypes.length ? prev : { ...prev, jobTypes: kept };
    });
  }, [setAnswers]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await loadDefinition(language);
        // Pre-fill the household block from what the parent has already entered
        // elsewhere. Best-effort: a failure here must not block the questionnaire.
        if (cancelled) return;
        try {
          const hh = await axios.get('/api/form-submissions/household-information');
          const d = (hh.data && (hh.data.household_data || hh.data.householdData || hh.data)) || {};
          if (!cancelled) {
            setAnswers((prev) => ({
              ...prev,
              // Mother/father names first, since that is what most households filled
              // in; fall back to the primary parent so a household that only recorded
              // one contact still gets a prefilled guardian line rather than a blank.
              parentsGuardians: prev.parentsGuardians || ([
                [d.mother_guardian_first_name, d.mother_guardian_last_name].filter(Boolean).join(' '),
                [d.father_guardian_first_name, d.father_guardian_last_name].filter(Boolean).join(' ')
              ].filter(Boolean).join(' / ')
                || [d.primary_parent_first_name, d.primary_parent_last_name].filter(Boolean).join(' '))
                // Enrollment data carries stray double spaces; this line is printed on a
                // state form, so collapse them.
                .replace(/\s+/g, ' ').trim(),
              address: prev.address || d.home_street_address || d.address_street || d.primary_mailing_address_street || '',
              city: prev.city || d.home_city || d.address_city || d.primary_mailing_address_city || '',
              state: prev.state || d.home_state || d.address_state || d.primary_mailing_address_state || '',
              zip: prev.zip || d.home_zip_code || d.address_zip || d.primary_mailing_address_zip || '',
              homePhone: prev.homePhone || d.home_phone || d.primary_household_phone || ''
            }));
          }
        } catch (hhErr) {
          // No household record yet - the parent can type the details in.
        }
      } catch (e) {
        if (!cancelled) setError('Could not load the questionnaire. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLanguage = async (lang) => {
    if (lang === language) return;
    setLanguage(lang);
    try {
      await loadDefinition(lang);
    } catch (e) {
      setError('Could not switch language. Please try again.');
    }
  };

  /**
   * Pre-fill the building from the student's grade.
   *
   * The building is DERIVED from grade when the form prints, so asking a parent to choose is
   * a formality - it is kept on the form for completeness. Pre-filling means the formality
   * costs them nothing and the stored answer matches what will print.
   *
   * Runs only while the field is empty, so it never fights a parent who changed it, and it
   * goes through setField so it works the same in embedded mode where the host owns answers.
   */
  useEffect(() => {
    if (!studentGrade) return;
    if (String(answers.schoolBuilding ?? '').trim()) return;
    const derived = buildingForGrade(studentGrade);
    if (derived) setField('schoolBuilding', derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentGrade, answers.schoolBuilding]);

  const setField = (name, value) => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
    // Clear a stale validation message once the parent starts fixing it.
    if (error) setError('');
  };

  const toggleJob = (key) => {
    setAnswers((prev) => ({
      ...prev,
      jobTypes: prev.jobTypes.includes(key)
        ? prev.jobTypes.filter((k) => k !== key)
        : [...prev.jobTypes, key]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Everything the official form has a line for, apart from the job pictures. Named
    // in the message and scrolled to, rather than a bare "fill in the form".
    const missing = missingMigrantAnswers(answers);
    if (missing.length) {
      setError(
        `Please complete ${missing.length === 1 ? 'this' : 'these'} before submitting: ` +
        `${missing.map((k) => MIGRANT_FIELD_LABELS[k] || k).join(', ')}.`
      );
      const first = document.getElementById(`field-migrant_${missing[0]}`);
      if (first) {
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = first.querySelector('input, textarea');
        if (input) input.focus({ preventScroll: true });
      }
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post('/api/migrant-questionnaire/submit', {
        studentIntakeId,
        answers: { ...answers, language }
      });
      if (onComplete) onComplete(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not submit the questionnaire. Please try again.');
      setSaving(false);
    }
  };

  if (loading) return <div className="mq-loading">Loading questionnaire…</div>;
  if (!definition) {
    return (
      <div className="mq">
        <div className="mq-error">{error || 'Questionnaire unavailable.'}</div>
      </div>
    );
  }

  const q = definition.questions;

  /**
   * One required text line of the official form. The id matches the host form's scroll
   * helper (#field-migrant_<key>) so a parent can tap it in the sticky missing-fields
   * bar and land on it, exactly like the rest of the enrollment form.
   */
  const textField = (key, spanClass = '', opts = {}) => {
    const { hint, type = 'text', ...inputProps } = opts;
    // A conditional line the current answers do not call for is genuinely optional right
    // now, so it must not carry an asterisk or show as missing - that was the whole
    // complaint about the date of move.
    const isRequired = !MIGRANT_CONDITIONAL_TEXT[key] || MIGRANT_CONDITIONAL_TEXT[key](answers);
    const isMissing = isRequired && String(answers[key] ?? '').trim() === '';
    return (
      <label
        key={key}
        className={`mq-field ${spanClass} ${isMissing ? 'is-missing' : ''}`}
        id={`field-migrant_${key}`}
      >
        <span>
          {MIGRANT_FIELD_LABELS[key]}{' '}
          {isRequired
            ? <span className="mq-required" aria-hidden="true">*</span>
            : <span className="mq-optional">(optional)</span>}
        </span>
        {/* aria-required, but deliberately NOT the native `required` attribute: when
            embedded, this input lives inside the enrollment form, which does not set
            noValidate. A native required would make the browser block that form with
            its own one-at-a-time bubble instead of the sticky missing-fields bar the
            rest of enrollment uses. The checking happens in JS in both modes. */}
        <input
          type={type}
          value={answers[key] ?? ''}
          aria-required={isRequired}
          onChange={(e) => setField(key, e.target.value)}
          {...inputProps}
        />
        {hint && <small className="mq-field-hint">{hint}</small>}
      </label>
    );
  };

  /**
   * A select with the same label, missing-detection and aria semantics as textField.
   *
   * Deliberately mirrors textField rather than generalising it: the same reason textField
   * avoids the native `required` attribute applies here. When embedded, this lives inside the
   * enrollment form, and a native required would hand validation to the browser's
   * one-at-a-time bubble instead of the sticky missing-fields bar the rest of enrollment uses.
   */
  const selectField = (key, options, spanClass = '', opts = {}) => {
    const { hint, placeholder = 'Choose…' } = opts;
    const isRequired = !MIGRANT_CONDITIONAL_TEXT[key] || MIGRANT_CONDITIONAL_TEXT[key](answers);
    const isMissing = isRequired && String(answers[key] ?? '').trim() === '';
    return (
      <label
        key={key}
        className={`mq-field ${spanClass} ${isMissing ? 'is-missing' : ''}`}
        id={`field-migrant_${key}`}
      >
        <span>
          {MIGRANT_FIELD_LABELS[key]}{' '}
          {isRequired
            ? <span className="mq-required" aria-hidden="true">*</span>
            : <span className="mq-optional">(optional)</span>}
        </span>
        <select
          value={answers[key] ?? ''}
          aria-required={isRequired}
          onChange={(e) => setField(key, e.target.value)}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {hint && <small className="mq-field-hint">{hint}</small>}
      </label>
    );
  };

  const YesNo = ({ name, label }) => (
    // id matches the host form's scroll helper, which looks for #field-<name>, so these
    // questions behave like every other required field in the sticky missing-fields bar.
    <div className="mq-question" id={`field-migrant_${name}`}>
      <p className="mq-question-text">{label}</p>
      <div className="mq-yesno" role="radiogroup" aria-label={label}>
        {[true, false].map((val) => (
          <label key={String(val)} className={`mq-yesno-option ${answers[name] === val ? 'is-selected' : ''}`}>
            <input
              type="radio"
              name={name}
              checked={answers[name] === val}
              onChange={() => setField(name, val)}
            />
            <span>{val ? (language === 'es' ? 'Sí' : language === 'fr' ? 'Oui' : 'Yes')
                       : (language === 'fr' ? 'Non' : 'No')}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const Wrapper = embedded ? 'div' : 'form';
  const wrapperProps = embedded ? { className: 'mq mq-embedded' } : { className: 'mq', onSubmit: handleSubmit };

  return (
    <Wrapper {...wrapperProps}>
      <div className="mq-header">
        {embedded
          ? <h3 style={{ margin: 0 }}>Migrant Education Parent Questionnaire</h3>
          : <h2>Migrant Education Parent Questionnaire</h2>}
        <div className="mq-langs" role="group" aria-label="Questionnaire language">
          {definition.languages.map((l) => (
            <button
              key={l.code}
              type="button"
              className={`mq-lang ${language === l.code ? 'is-active' : ''}`}
              onClick={() => changeLanguage(l.code)}
            >
              {l.name}
            </button>
          ))}
        </div>
      </div>

      <p className="mq-intro">
        The state of Missouri asks every family this short questionnaire at enrollment. Most families
        answer “no” to all three questions. Your answers may qualify your child for free additional
        educational services.
        {studentName && !embedded ? <> This questionnaire is for <strong>{studentName}</strong>.</> : null}
      </p>

      {(error || externalError) && <div className="mq-error">{error || externalError}</div>}

      <section className="mq-section">
        <YesNo name="movedIntoDistrict" label={q.movedIntoDistrict} />
        <YesNo name="workedAgFishing" label={q.workedAgFishing} />
        <YesNo name="plansAgFishing" label={q.plansAgFishing} />
      </section>

      <section className="mq-section">
        <p className="mq-question-text">{q.jobPrompt}</p>
        <p className="mq-hint">Select any that apply — or none, if none apply.</p>
        <div className="mq-tiles">
          {definition.jobOptions.map((opt) => {
            const selected = answers.jobTypes.includes(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                className={`mq-tile ${selected ? 'is-selected' : ''}`}
                aria-pressed={selected}
                onClick={() => toggleJob(opt.key)}
              >
                <span className="mq-tile-check" aria-hidden="true">{selected ? '✓' : ''}</span>
                <span className="mq-tile-label">{opt.label}</span>
              </button>
            );
          })}
        </div>
        <label className="mq-field mq-other">
          <span>{q.otherLabel}</span>
          <input
            type="text"
            value={answers.jobTypesOther}
            maxLength={60}
            onChange={(e) => setField('jobTypesOther', e.target.value)}
            placeholder="Other type of work (optional)"
          />
        </label>
      </section>

      <section className="mq-section">
        <h3 className="mq-section-title">Household information</h3>
        <p className="mq-hint">
          Pre-filled from your household details — correct anything that is different for this
          form. Changes here apply to this questionnaire only and do not alter your household
          record.
        </p>
        <div className="mq-grid">
          {textField('parentsGuardians', 'mq-span2')}
          {textField('address', 'mq-span2')}
          {textField('city')}
          {textField('state', '', { maxLength: 20 })}
          {textField('zip', '', { maxLength: 10 })}
          {textField('homePhone', '', { type: 'tel', inputMode: 'tel' })}
          {textField('placeOfEmployment')}
          {textField('childrenInHome', '', { maxLength: 3, inputMode: 'numeric' })}
          {textField('dateOfMove', '', {
            placeholder: 'MM/YYYY',
            hint: answers.movedIntoDistrict === true
              ? 'When you moved into the district'
              : 'Only if you moved into the district'
          })}
          {/*
            Two options, per Brandy, split at 7th grade; Preschool reports as Elementary.
            This was free text and produced the mess that motivated the change - "E", "C",
            "?", the district's own name, and ~30 answers naming a junior high that is not
            its own building.

            Not pre-filled from grade, because this component is given the student's name but
            not their grade, and threading it through three components for a convenience is
            not worth it. The backend derives the building from the student's grade when it
            prints, so a blank or legacy answer still comes out right.
          */}
          {selectField('schoolBuilding', BUILDING_OPTIONS, '', {
            placeholder: 'Choose a building…',
            hint: `Which building ${studentName ? studentName.split(' ')[0] : 'this student'} attends`
          })}
        </div>
      </section>

      {!embedded && (
        <div className="mq-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Submitting…' : 'Submit Questionnaire'}
          </button>
          {onCancel && (
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
        </div>
      )}
      <p className="mq-revision">Form {definition.revision}</p>
    </Wrapper>
  );
};

export default MigrantQuestionnaire;
